'use client';

import { motion } from 'framer-motion';
import { forwardRef, useEffect, useImperativeHandle, useRef, type ReactNode } from 'react';

import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { risePart } from './topics-motion';

/**
 * The confirmation that replaces the form card after a successful send (the design has no frame for
 * it): the card frame of 232:14151 with the green check, core „Kontaktný formulár bol úspešne
 * odoslaný“ (H4) and the response time of the topic (Body/L), then the next step (`children`, a
 * button) when there is one. A status region that also takes the focus (`tabIndex -1`) – the submit
 * button it replaces is gone: the caller focuses it through the ref, or `focusOnMount` does when it
 * appears. The step variants of the contact flow reach it as `risePart`; elsewhere it stands still.
 */
export const TopicSent = forwardRef<
  HTMLDivElement,
  {
    title: string;
    text?: string;
    focusOnMount?: boolean;
    className?: string;
    children?: ReactNode;
  }
>(function TopicSent({ title, text, focusOnMount, className, children }, ref) {
  const region = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => region.current as HTMLDivElement, []);
  useEffect(() => {
    if (focusOnMount) region.current?.focus();
  }, [focusOnMount]);

  return (
    <motion.div
      ref={region}
      variants={risePart}
      role="status"
      tabIndex={-1}
      className={cn(
        'mx-auto flex w-full max-w-[878px] flex-col items-center gap-4 rounded-2xl border border-[#E2E5DF] bg-white px-4 py-10 text-center outline-none md:px-8 md:py-14',
        className,
      )}
    >
      <span className="flex size-16 items-center justify-center rounded-full bg-[#E7F4E9]">
        <Icon name="check-circle" size={32} className="text-[#127A26]" />
      </span>
      <p className="nt-h4 text-[#0D110D]">{title}</p>
      {text ? <p className="nt-body-l text-[#363D36]">{text}</p> : null}
      {children}
    </motion.div>
  );
});
